<template>
  <HomeLayout>
    <b-container class="py-4">
      <b-card>
        <h5 class="mb-1">Retention Simulator</h5>
        <p class="text-muted mb-4" style="font-size:0.85rem">
          Dev-only tool. Manipulate campaign retention state and trigger scheduler steps without waiting for real dates.
        </p>

        <!-- Campaign lookup -->
        <b-row class="mb-4">
          <b-col md="6">
            <label class="font-weight-bold">Campaign ID</label>
            <b-input-group>
              <b-form-input v-model="campaignId" placeholder="MongoDB campaign _id" />
              <b-input-group-append>
                <b-button variant="primary" :disabled="!campaignId || loadingState" @click="loadState">
                  <span v-if="loadingState"><b-spinner small /></span>
                  <span v-else>Load State</span>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>

        <!-- Current state -->
        <b-card v-if="state" bg-variant="light" class="mb-4">
          <h6 class="mb-3">Current Retention State</h6>
          <b-row>
            <b-col
              v-for="(val, key) in stateFields"
              :key="key"
              md="4"
              class="mb-2"
            >
              <div class="text-muted" style="font-size:0.75rem">{{ key }}</div>
              <div :class="fieldClass(key, val)" style="font-size:0.85rem; font-weight:500; word-break:break-all">
                {{ formatVal(val) }}
              </div>
            </b-col>
          </b-row>
        </b-card>

        <template v-if="state">
          <!-- Step 1: Set election date -->
          <b-card class="mb-3">
            <b-row align-v="center">
              <b-col>
                <strong>Step 1 — Set Election Date to the Past</strong>
                <div class="text-muted" style="font-size:0.8rem">
                  Makes the campaign appear post-election so the retention scan picks it up.
                </div>
              </b-col>
              <b-col md="3">
                <b-input-group prepend="Days ago">
                  <b-form-input v-model.number="daysAgo" type="number" min="1" />
                </b-input-group>
              </b-col>
              <b-col md="2" class="text-right">
                <b-button variant="warning" block :disabled="running" @click="run('set_election_date')">
                  Apply
                </b-button>
              </b-col>
            </b-row>
          </b-card>

          <!-- Step 2: Trigger retention scan -->
          <b-card class="mb-3">
            <b-row align-v="center">
              <b-col>
                <strong>Step 2 — Run Retention Scan</strong>
                <div class="text-muted" style="font-size:0.8rem">
                  Runs <code>accountRetention()</code>: sets can_retain, can_retain_until, enqueues retention email.
                </div>
              </b-col>
              <b-col md="2" class="text-right">
                <b-button variant="warning" block :disabled="running" @click="run('run_retention_scan')">
                  Run
                </b-button>
              </b-col>
            </b-row>
          </b-card>

          <!-- Step 3: Expire retention window -->
          <b-card class="mb-3">
            <b-row align-v="center">
              <b-col>
                <strong>Step 3 — Expire Retention Window</strong>
                <div class="text-muted" style="font-size:0.8rem">
                  Sets <code>can_retain_until</code> to 1 minute ago. Simulates the window closing without waiting 30 days.
                  Do this <em>before</em> running the delete scan if you want to test non-retained deletion.
                </div>
              </b-col>
              <b-col md="2" class="text-right">
                <b-button variant="warning" block :disabled="running" @click="run('expire_retention_window')">
                  Expire
                </b-button>
              </b-col>
            </b-row>
          </b-card>

          <!-- Step 4: Trigger delete scan -->
          <b-card class="mb-3">
            <b-row align-v="center">
              <b-col>
                <strong>Step 4 — Run Delete Scan</strong>
                <div class="text-muted" style="font-size:0.8rem">
                  Runs <code>scanDelete()</code>: deletes campaigns that missed the retention window or hit the reactivation limit.
                </div>
              </b-col>
              <b-col md="2" class="text-right">
                <b-button variant="danger" block :disabled="running" @click="run('run_delete_scan')">
                  Run
                </b-button>
              </b-col>
            </b-row>
          </b-card>

          <hr />

          <!-- Shortcuts -->
          <b-row class="mb-3">
            <b-col md="4">
              <b-button variant="primary" block :disabled="running" @click="run('run_all')">
                <span v-if="running && activeAction === 'run_all'"><b-spinner small /> Running...</span>
                <span v-else>Run Full Flow (Steps 2–4)</span>
              </b-button>
            </b-col>
            <b-col md="4">
              <b-button variant="outline-secondary" block :disabled="running" @click="run('reset')">
                Reset All Retention Fields
              </b-button>
            </b-col>
          </b-row>

          <!-- Result -->
          <b-alert v-if="result" :variant="result.ok ? 'success' : 'danger'" show dismissible @dismissed="result = null">
            {{ result.message || result.error }}
          </b-alert>
        </template>
      </b-card>
    </b-container>
  </HomeLayout>
</template>

<script>
import HomeLayout from "@/layout/HomeLayout.vue";
import { GetRetentionState, SimulateRetention } from "@/services/retentionSimulator.service";

const DATE_FIELDS = ['dateOfElection', 'can_retain_until', 'retained_date', 'retention_email_after', 'retention_email_before'];
const BOOL_FIELDS = ['can_retain', 'retained', 'retention_email_sent', 'perma_deleted', 'enabled', 'disabledBySystem'];

export default {
  name: "RetentionSimulator",
  components: { HomeLayout },
  data() {
    return {
      campaignId: "",
      daysAgo: 3,
      state: null,
      loadingState: false,
      running: false,
      activeAction: null,
      result: null,
    };
  },
  computed: {
    stateFields() {
      if (!this.state) return {};
      // eslint-disable-next-line no-unused-vars
      const { _id, campaign, ...rest } = this.state;
      return rest;
    },
  },
  methods: {
    async loadState() {
      this.loadingState = true;
      this.result = null;
      const res = await GetRetentionState(this.campaignId);
      this.loadingState = false;
      if (res?.data && !res.data.error) {
        this.state = res.data;
      } else {
        this.result = { ok: false, error: res?.data?.error || "Failed to load state" };
      }
    },
    async run(action) {
      this.running = true;
      this.activeAction = action;
      this.result = null;
      const res = await SimulateRetention(this.campaignId, action, this.daysAgo);
      this.running = false;
      this.activeAction = null;
      if (res?.data) {
        this.result = res.data;
      } else {
        this.result = { ok: false, error: "Request failed" };
      }
      await this.loadState();
    },
    formatVal(val) {
      if (val === null || val === undefined) return "—";
      if (typeof val === "boolean") return val ? "true" : "false";
      if (DATE_FIELDS.some(f => this.state && Object.keys(this.state).includes(f))) {
        // check if val looks like a date
        const d = new Date(val);
        if (!isNaN(d.getTime()) && typeof val === "string") return d.toLocaleString();
      }
      return String(val);
    },
    fieldClass(key, val) {
      if (BOOL_FIELDS.includes(key)) {
        if (key === 'enabled') return val ? 'text-success' : 'text-danger';
        if (key === 'perma_deleted' || key === 'disabledBySystem') return val ? 'text-danger' : '';
        return val ? 'text-success' : '';
      }
      return '';
    },
  },
};
</script>
